/**
 * Clients — couche API (page V2 « Clients »)
 *
 * Liste paginée des clients (companies actives), recherche client-only côté
 * serveur (PAS l'omnisearch global). S'appuie sur `GET /companies/list` (boostcore),
 * qui déduplique la cohabitation Odoo v16/v18 selon la source de l'user.
 */
import { apiFetch } from '@/auth/apiFetch'

export interface ClientListItem {
  uuid: string
  name: string | null
  siren: string | null
  siret: string | null
  vat: string | null
  zip: string | null
  referenceExterne: string | null
  /** Statut métier Odoo, stocké en minuscule : client / prospect / suspect / … */
  currentStatus: string | null
}

export interface ClientListResponse {
  data: ClientListItem[]
  total: number
}

export type ClientSortField = 'name' | 'siret' | 'siren' | 'zip'
export type SortOrder = 'asc' | 'desc'
/** Filtre statut : un statut métier précis, ou 'all' (= aucun filtre). */
export type ClientStatusFilter = 'client' | 'prospect' | 'suspect' | 'all'

function qs(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  if (!entries.length) return ''
  const usp = new URLSearchParams()
  for (const [k, v] of entries) usp.set(k, String(v))
  return `?${usp.toString()}`
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
  if (res.status === 204) return undefined as unknown as T
  return (await res.json()) as T
}

export function listClients(params: {
  q?: string
  status?: string
  limit: number
  offset: number
  sort: ClientSortField
  order: SortOrder
  signal?: AbortSignal
}): Promise<ClientListResponse> {
  const { q, status, limit, offset, sort, order, signal } = params
  return apiFetch(`/companies/list${qs({ q, status, limit, offset, sort, order })}`, { signal }).then(
    json<ClientListResponse>,
  )
}

/**
 * Fiche société (boostcore) par uuid — renvoie le détail non fourni par
 * `/companies/list` : code NAF (+ libellé / secteur d'activité) et adresse.
 * Mêmes données que celles consommées par la fiche client 360.
 */
export interface ClientDetails {
  uuid: string
  name: string | null
  /** NAF : soit un code brut, soit `{ code, label }` (label = secteur d'activité). */
  nafCode: string | { code: string | null; label: string | null } | null
  street: string | null
  zip: string | null
  city: string | null
  metadata?: { street?: string | null; city?: string | null } | null
}

export function getClientDetails(uuid: string, signal?: AbortSignal): Promise<ClientDetails> {
  return apiFetch(`/companies/${uuid}`, { signal }).then(json<ClientDetails>)
}
